<!-- `is-dim` for a non-active status hidden 2026-09-25 with the "Inactive" chip (workspaceMarkers) —
     status gates nothing yet. Was: [class.is-dim]="!!workspace.status && workspace.status !== 'active'" -->
<!-- A deleted workspace is dimmed: its row stays only as a record of what was there. -->
<div class="uw-ws" [class.is-open]="open" [class.is-dim]="!!deletionNote()?.deleted">

  <!-- ═══ Collapsed row — click anywhere but the name to expand/collapse ═══ -->
  <div class="uw-ws-head" (click)="onToggle()">

    <!-- Identity tile: a monogram keyed to the workspace's id (a rename never repaints it) — or, for
         the builders' own workspace, the team's mark instead of a mystery "DE". -->
    <div class="uw-tile" [ngClass]="workspace.isBuilders ? 'uw-tile-builders' : tileFill()" aria-hidden="true">
      <svg *ngIf="workspace.isBuilders" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-[18px] h-[18px]" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 12-8.4 8.4a2.1 2.1 0 1 1-3-3L12 9"/><path d="M17.6 15 22 10.6"/><path d="m20.9 11.7-1.3-1.3c-.6-.6-.9-1.3-.9-2.1V7.1l-2.4-2.3a5.4 5.4 0 0 0-3.8-1.6H9.6l.9.8a5.9 5.9 0 0 1 1.9 4.4v1.4l2 2h2.3"/></svg>
      <span *ngIf="!workspace.isBuilders">{{ tileText() }}</span>
    </div>

    <div class="min-w-0 flex-1">
      <div class="flex items-center gap-2 flex-wrap">

        <!-- Name: view mode, click to rename in place — EXCEPT on the builders' workspace, which has
             no rename affordance at all (fix-round-3 Critical 1). Its name is what this whole project
             finds that workspace by; renaming it unbinds the Developer-mode gate, `isBuilders`,
             preview invite routing and Track-D role injection in one keystroke. The server refuses it
             too (409); this is so the row never offers the click in the first place. `renameControl`
             (workspaces.util.ts) owns the decision AND the sentence explaining it. -->
        <span *ngIf="!editingName() && renameControl(workspace).enabled" (click)="startEditName($event)" title="Click to rename"
              class="group/name inline-flex items-center gap-1.5 min-w-0 text-[14px] font-semibold text-(--uw-strong) cursor-text">
          <span class="truncate decoration-dotted decoration-(--uw-faint) underline-offset-4 group-hover/name:underline">{{ workspace.name }}</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
               class="w-3 h-3 shrink-0 text-(--uw-faint) opacity-0 group-hover/name:opacity-100 transition-opacity" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
        </span>
        <span *ngIf="!editingName() && !renameControl(workspace).enabled" [title]="renameControl(workspace).reason"
              class="text-[14px] font-semibold text-(--uw-strong) truncate">
          {{ workspace.name }}
        </span>

        <!-- Name: edit mode. Enter saves, Escape cancels, blur saves. -->
        <input *ngIf="editingName()" #nameInput type="text" [(ngModel)]="nameDraft" maxlength="60"
               (click)="$event.stopPropagation()"
               (keyup.enter)="saveEditName()"
               (keyup.escape)="cancelEditName()"
               (blur)="saveEditName()"
               [disabled]="renaming()"
               aria-label="Workspace name"
               class="uw-field h-8 px-2.5 text-[13.5px] font-semibold w-64" />

        <span *ngIf="renaming()" class="uw-live"><i></i>Saving…</span>

        <span *ngIf="workspace.isBuilders" class="uw-chip uw-chip-brand">Your team</span>
        <span *ngFor="let m of workspaceMarkers(workspace, switches, { developerModeOn: developerModeOn })" class="uw-chip">{{ m }}</span>
        <span *ngIf="deletionNote() as d" class="uw-chip" [class.uw-chip-danger]="!d.deleted" data-test="deletion-chip">{{ d.chip }}</span>
      </div>

      <!-- A workspace being deleted: when it goes for good, and, while it is pending, a builder's way back.
           Every click here stops at this block, because the header around it toggles the row. -->
      <div *ngIf="deletionNote() as d" class="mt-1 flex items-center gap-2 flex-wrap" (click)="$event.stopPropagation()">
        <p class="text-[11.5px] font-medium" [ngClass]="d.deleted ? 'text-(--uw-muted)' : 'text-rose-600'" data-test="deletion-date">{{ d.line }}</p>
        <button *ngIf="canRestore() && !confirmingRestore()" type="button" (click)="askRestore($event)"
                class="uw-btn uw-btn-ghost h-7 px-2.5 text-[11.5px]" data-test="restore-workspace">Restore workspace</button>
      </div>
      <div *ngIf="confirmingRestore()" class="uw-callout uw-callout-warn is-sm mt-2 flex-wrap" (click)="$event.stopPropagation()" role="group" aria-label="Restore workspace">
        <span class="flex-1 min-w-[14rem]">Restore {{ workspace.name }}? {{ RESTORE_CONFIRM }}</span>
        <button type="button" (click)="cancelRestore($event)" [disabled]="restoring()" class="uw-btn uw-btn-ghost h-7 px-2.5 text-[11.5px]">Cancel</button>
        <button type="button" (click)="confirmRestore($event)" [disabled]="restoring()" [attr.data-mcp-button]="pressing() === 'restore-workspace' ? 'Restore' : null" class="uw-btn uw-btn-primary h-7 px-2.5 text-[11.5px]" data-test="restore-confirm">
          {{ restoring() ? 'Restoring…' : 'Restore' }}
        </button>
      </div>
      <p *ngIf="restoreError()" class="text-[11.5px] text-rose-500 mt-1">{{ restoreError() }}</p>
      <p *ngIf="noAdminHint() as h" class="text-[11.5px] font-medium text-amber-600 mt-1" data-test="no-admin-hint">{{ h }}</p>

      <!-- Task 11: "Acme · 2 of 14 match" — the tab's own filterWorkspace(...).countLabel, empty
           (and so hidden) whenever no search is narrowing this workspace's people. -->
      <p *ngIf="countLabel" class="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-orange-500">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="w-3 h-3" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
        {{ countLabel }}
      </p>

      <p *ngIf="renameError()" class="text-[11.5px] text-rose-500 mt-1">{{ renameError() }}</p>
    </div>

    <!-- Seats: seatLabel's words, always — with seatMeter's picture of them beside, when there is a cap
         to be a fraction of. Full and over add an icon, so the state never rests on colour alone. -->
    <div class="uw-meter hidden sm:flex flex-col items-end gap-1.5 shrink-0"
         [class.is-full]="meter().state === 'full'" [class.is-over]="meter().state === 'over'">
      <div *ngIf="meter().pips as pips" class="uw-pips" aria-hidden="true">
        <i *ngFor="let taken of pips" class="uw-pip" [class.is-on]="taken"></i>
        <span *ngIf="meter().overflow" class="ml-1 text-[10.5px] font-bold text-rose-500">+{{ meter().overflow }}</span>
      </div>
      <div *ngIf="!meter().pips && meter().state !== 'uncapped'" class="uw-bar" aria-hidden="true">
        <i [style.width.%]="meter().percent"></i>
      </div>
      <span class="uw-meter-label">
        <svg *ngIf="meter().state === 'full' || meter().state === 'over'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" class="w-3 h-3" aria-hidden="true"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4M12 17h.01"/></svg>
        <svg *ngIf="meter().state === 'uncapped'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5 text-(--uw-faint)" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        {{ seatLabel(workspace) }}
      </span>
    </div>
    <!-- Narrow screens drop the picture but never the words. -->
    <span class="sm:hidden shrink-0"><span class="uw-meter-label">{{ seatLabel(workspace) }}</span></span>

    <!-- Chevron — a real button, so a row opens from the keyboard too. It has no handler of its own:
         its click bubbles to the header's (click)="onToggle()", which already toggles; a second
         handler here would toggle twice. -->
    <button type="button" class="uw-chevron" [attr.aria-expanded]="open"
            [attr.aria-label]="(open ? 'Collapse ' : 'Expand ') + workspace.name">
      <svg class="h-4 w-4" [class.rotate-90]="open" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.25">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
      </svg>
    </button>
  </div>

  <!-- ═══ Expanded content — Task 7 fills who is here; Tasks 8-10 add invitations/settings ═══ -->
  <div *ngIf="open" class="uw-ws-body uw-reveal">

    <!-- ═══ Settings strip — Task 10. Above the add-person form on purpose: that form's own "shut
         doors" note says "Turn one on in the switches above", which is only true placed here. ═══ -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-3">

      <!-- Seat cap -->
      <div class="uw-setting">
        <label class="uw-setting-label" [attr.for]="'seat-cap-' + workspace.id">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5"><path d="M19 9V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v3"/><path d="M3 16a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v1.5a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5V11a2 2 0 0 0-4 0z"/><path d="M5 18v2M19 18v2"/></svg>
          Seat cap
        </label>
        <div class="flex items-center gap-2.5 mt-3 flex-wrap">
          <div class="uw-stepper">
            <button type="button" (click)="nudgeSeatCap(-1)" aria-label="One seat fewer"
                    [disabled]="seatCapUnlimited || !seatCapControl(workspace, access).enabled || savingSeatCap() || (seatCapDraft !== null && seatCapDraft <= 1)">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="w-3 h-3"><path d="M5 12h14"/></svg>
            </button>
            <input type="number" min="1" [id]="'seat-cap-' + workspace.id" [(ngModel)]="seatCapDraft" (ngModelChange)="onSeatCapEdited()"
                   [disabled]="seatCapUnlimited || !seatCapControl(workspace, access).enabled || savingSeatCap()"
                   [attr.placeholder]="seatCapUnlimited ? '∞' : null" />
            <button type="button" (click)="nudgeSeatCap(1)" aria-label="One seat more"
                    [disabled]="seatCapUnlimited || !seatCapControl(workspace, access).enabled || savingSeatCap()">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="w-3 h-3"><path d="M12 5v14M5 12h14"/></svg>
            </button>
          </div>
          <label class="flex items-center gap-1.5 text-xs font-medium text-(--uw-text) select-none"
                 [class.cursor-pointer]="seatCapControl(workspace, access).enabled && !savingSeatCap()">
            <input type="checkbox" [(ngModel)]="seatCapUnlimited" (ngModelChange)="onSeatCapEdited()"
                   [disabled]="!seatCapControl(workspace, access).enabled || savingSeatCap()"
                   class="w-3.5 h-3.5 accent-orange-500 cursor-pointer disabled:cursor-not-allowed" />
            Unlimited
          </label>
          <button type="button" (click)="saveSeatCap()" [disabled]="!canSaveSeatCap()" [attr.data-mcp-button]="pressing() === 'set-seat-cap' ? 'Save' : null"
                  class="uw-btn h-[30px] px-3 text-[11.5px] ml-auto"
                  [ngClass]="hasSeatCapEdit() ? 'uw-btn-primary' : 'uw-btn-quiet'">
            {{ savingSeatCap() ? 'Saving…' : 'Save' }}
          </button>
        </div>

        <!-- Disabled reason — owner-only, or the builders' workspace. THE BUILDERS' WORKSPACE CAP IS
             DISABLED HERE ON PURPOSE (see seatCapControl in workspaces.util.ts). The server refuses
             it as well; this is so a builder reads the rule instead of meeting it as a 409. -->
        <p *ngIf="!seatCapControl(workspace, access).enabled" class="uw-reason mt-2.5">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-3 h-3" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
          <span>{{ seatCapControl(workspace, access).reason }}</span>
        </p>
        <!-- The other thing builders guess wrong about a cap: it never evicts anybody. Exported
             (workspaces.util.ts's SEAT_CAP_LOWERING_NOTE) rather than typed here — fix-round-1
             finding 3, the third time this plan has had page copy shipped straight into a template. -->
        <p class="uw-setting-note mt-2.5">{{ SEAT_CAP_LOWERING_NOTE }}</p>
        <p *ngIf="seatCapError()" class="text-[11.5px] text-rose-500 mt-2">{{ seatCapError() }}</p>
        <!-- From THIS save's own response (setSeatLimit returns activeCount/overshoot) — said here
             immediately rather than left silent until the tab's reload catches up. States the fact
             only (seatOvershootLine) — the "nobody was removed" rule already lives once, in
             SEAT_CAP_LOWERING_NOTE right above; fix-round-1 finding 3 was this restating it a
             second time, in different words, in the same column. -->
        <p *ngIf="seatCapOvershoot() as o" class="uw-callout uw-callout-warn is-sm mt-2.5">
          Saved. {{ seatOvershootLine(o.activeCount, o.limit) }}
        </p>
      </div>

      <!-- Allow invitations. The button (not a checkbox) reads workspace.invitesEnabled directly —
           fix-round-1 finding 2: a checkbox the user clicks changes the DOM, and on a failed flip
           this component sets an error and returns without emitting `changed`, so the bound
           `[checked]` expression never changes and Angular never rewrites it back — the box stayed
           flipped, out of step with the server, forever. A button has no state of its own to fall
           out of step: it always reads straight from the model, matching the project-wide switches'
           own idiom five sections above. -->
      <div class="uw-setting">
        <div class="flex items-center justify-between gap-2">
          <p class="uw-setting-label">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            Allow invitations
          </p>
          <div class="flex items-center gap-2">
            <span class="uw-switch-state" [class.is-on]="workspace.invitesEnabled && !isSavingSwitch('invites')">{{ isSavingSwitch('invites') ? 'Saving…' : (workspace.invitesEnabled ? 'On' : 'Off') }}</span>
            <button type="button" role="switch" class="uw-switch" [class.is-busy]="isSavingSwitch('invites')"
                    [attr.aria-checked]="workspace.invitesEnabled" [attr.aria-label]="'Allow invitations into ' + workspace.name"
                    (click)="flipSwitch('invites', !workspace.invitesEnabled)"
                    [disabled]="!!savingSwitch() || readOnly()"></button>
          </div>
        </div>
        <!-- Fix-round-1 finding 1 (CRITICAL): never the open-copy unconditionally — doors().invite
             is the exact same verdict `<app-add-person>` six lines below reads, so this can no
             longer say "open" while that form says the project (or Developer mode) has shut it. -->
        <p class="uw-setting-note mt-3" [class.is-warn]="!doors().invite.enabled">{{ switchDescription(doors().invite, ALLOW_INVITATIONS_COPY) }}</p>
      </div>

      <!-- Allow direct adds — same button idiom and the same doors()-routed description as invitations above. -->
      <div class="uw-setting">
        <div class="flex items-center justify-between gap-2">
          <p class="uw-setting-label">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5"><circle cx="7.5" cy="15.5" r="5.5"/><path d="m21 2-9.6 9.6M15.5 7.5l3 3L22 7l-3-3"/></svg>
            Allow direct adds
          </p>
          <div class="flex items-center gap-2">
            <span class="uw-switch-state" [class.is-on]="workspace.addsEnabled && !isSavingSwitch('adds')">{{ isSavingSwitch('adds') ? 'Saving…' : (workspace.addsEnabled ? 'On' : 'Off') }}</span>
            <button type="button" role="switch" class="uw-switch" [class.is-busy]="isSavingSwitch('adds')"
                    [attr.aria-checked]="workspace.addsEnabled" [attr.aria-label]="'Allow direct adds into ' + workspace.name"
                    (click)="flipSwitch('adds', !workspace.addsEnabled)"
                    [disabled]="!!savingSwitch() || readOnly()"></button>
          </div>
        </div>
        <p class="uw-setting-note mt-3" [class.is-warn]="!doors().add.enabled">{{ switchDescription(doors().add, ALLOW_DIRECT_ADDS_COPY) }}</p>
      </div>

      <!-- ws 9b-2 W14e — the Arrival notice. Same button idiom as the two switches above. -->
      <div class="uw-setting">
        <div class="flex items-center justify-between gap-2">
          <p class="uw-setting-label">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
            {{ ARRIVAL_NOTICE_LABEL }}
          </p>
          <div class="flex items-center gap-2">
            <span class="uw-switch-state" [class.is-on]="arrivalNoticeOn(workspace) && !isSavingSwitch('arrivals')">{{ isSavingSwitch('arrivals') ? 'Saving…' : (arrivalNoticeOn(workspace) ? 'On' : 'Off') }}</span>
            <button type="button" role="switch" class="uw-switch" [class.is-busy]="isSavingSwitch('arrivals')"
                    [attr.aria-checked]="arrivalNoticeOn(workspace)" [attr.aria-label]="ARRIVAL_NOTICE_LABEL + ' — ' + workspace.name"
                    (click)="flipSwitch('arrivals', !arrivalNoticeOn(workspace))"
                    [disabled]="!!savingSwitch() || readOnly()"></button>
          </div>
        </div>
        <p class="uw-setting-note mt-3">{{ ARRIVAL_NOTICE_COPY }}</p>
      </div>
    </div>

    <p *ngIf="switchError()" class="uw-callout uw-callout-danger mt-3">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-4 h-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4.5M12 16h.01"/></svg>
      <span>{{ switchError() }}</span>
    </p>

    <!-- ONE audit line for the whole strip, not one per control: the server stores a single shared
         settings_changed_by/at pair across the seat cap and the three switches (workspace-store.ts's
         setSeatLimit/setWorkspaceSwitch both write the same two columns), so it can only say when
         something here last changed and by whom — never which of the three it was. -->
    <p *ngIf="auditLine(workspace.settingsChangedBy, workspace.settingsChangedAt) as line"
       class="uw-audit mt-2.5 px-1">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-3 h-3" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
      {{ line }}
    </p>

    <!-- ═══ People ═══ -->
    <div class="mt-6">
      <div class="uw-subhead">
        <h3>People</h3>
        <!-- Count: activePeople(...).length, NEVER users().length — listUsers includes removed people
             on purpose, and a count that forgets disagrees with the seat numbers above. Suppressed at
             zero — it would otherwise contradict the empty state below it. -->
        <p *ngIf="usersLoaded && activePeople(users()).length > 0" class="text-[11.5px] text-(--uw-muted)">
          {{ activePeople(users()).length === 1 ? '1 person holds a seat' : (activePeople(users()).length + ' people hold seats') }}
        </p>
        <p *ngIf="refreshingUsers()" class="uw-live" aria-live="polite"><i></i>Refreshing…</p>
      </div>

      <!-- ═══ Add someone — Task 9. Above the people list: the workspace is THIS row, not a dropdown. ═══ -->
      <app-add-person *ngIf="!readOnly()" [workspace]="workspace" [switches]="switches" [developerModeOn]="developerModeOn" (added)="onAdded($event)" />

      <!-- Server error, shown as-is — never replaced. A failed refresh (post-removal, say) must not
           blank a list that was already good, so this never gates the body below — usersLoaded does —
           and "Try again" re-runs the same load instead of leaving collapse/re-expand as the only way. -->
      <div *ngIf="usersError()" class="uw-callout uw-callout-danger mb-3">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-4 h-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4.5M12 16h.01"/></svg>
        <span class="flex-1">{{ usersError() }}</span>
        <button type="button" (click)="loadUsers()" class="shrink-0 font-semibold underline underline-offset-2 cursor-pointer">Try again</button>
      </div>

      <!-- Loading: three skeleton rows, never a spinner. Shown only before the FIRST successful load —
           once usersLoaded is true, a later reload (or a failed one) uses refreshingUsers instead, so
           the list underneath stays mounted and visible. -->
      <div *ngIf="usersLoading()" class="space-y-2" aria-hidden="true">
        <div *ngFor="let i of [1,2,3]" class="uw-skel h-[54px]"></div>
      </div>

      <ng-container *ngIf="usersLoaded">

        <!-- Written empty state -->
        <div *ngIf="activePeople(users()).length === 0" class="flex items-center gap-3 px-3 py-3 rounded-xl border border-dashed border-(--uw-line-strong)">
          <div class="uw-avatar uw-avatar-quiet">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <p class="text-xs text-(--uw-muted)">Nobody holds a seat in this workspace.</p>
        </div>

        <!-- Fix-round-3 Critical 2: this workspace's people could not be fetched for the search, so
             nothing below has been checked against the term. Everyone is shown (the tab sends
             `matchedUserIds: null` for exactly this case), and this says why — instead of the row
             blanking its people and announcing a negative underneath the page's own banner admitting
             the search could not look everywhere. -->
        <p *ngIf="searchFailed" class="uw-callout uw-callout-warn mb-2" aria-live="polite">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-4 h-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4M12 17h.01"/></svg>
          <span>{{ SEARCH_WORKSPACE_NOT_CHECKED }}</span>
        </p>

        <!-- Fix-round-1 #3: a search that matched nobody currently active still needs saying so
             SEPARATELY from "Nobody holds a seat" above, which is a fact about total occupancy and
             would be false here — there could easily be 14 people, none of them a match. It is
             `matchedUserIds` that gates this, and a workspace the search could not load has none
             (null), so the claim can no longer be made about a workspace nobody checked. -->
        <p *ngIf="matchedUserIds && visibleActivePeople().length === 0 && activePeople(users()).length > 0"
           class="text-xs text-(--uw-muted) italic py-2 px-1">
          No active match here for this search — check Removed below.
        </p>

        <!-- ═══ The people list — narrowed to visibleActivePeople() while a search is active, per
             filterWorkspace's own `people` (fix-round-1 #3: this used to always render everyone,
             disagreeing with the "N of M match" count beside the name above). ═══ -->
        <div *ngIf="visibleActivePeople().length > 0" class="space-y-1.5">
          <ng-container *ngFor="let u of visibleActivePeople()"><div class="group uw-row" [class.is-dim]="hasNoAccess(u)">

            <!-- Avatar initials, on a colour keyed to the person's id -->
            <div class="uw-avatar" [ngClass]="avatarFill(u)">{{ initials(u) }}</div>

            <!-- Identity: name, then email or username beneath it — each with its own mark, so a
                 username-only person no longer reads as the same word twice. -->
            <div class="uw-row-text min-w-0 flex-1">
              <div class="text-[13px] font-medium text-(--uw-strong) truncate">{{ displayName(u) }}</div>
              <div class="text-[11.5px] text-(--uw-muted) truncate flex items-center gap-1.5">
                <svg *ngIf="u.email" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3 h-3 shrink-0 text-(--uw-faint)" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                <svg *ngIf="!u.email && u.loginUsername" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3 h-3 shrink-0 text-(--uw-faint)" aria-hidden="true"><circle cx="7.5" cy="15.5" r="5.5"/><path d="m21 2-9.6 9.6M15.5 7.5l3 3L22 7l-3-3"/></svg>
                <span class="truncate">{{ u.email || u.loginUsername || '—' }}</span>
                <span *ngIf="!u.email && u.loginUsername" class="text-(--uw-faint)">· username</span>
              </div>
              <!-- A refused role change, the server's own words (the last admin, another builder's role). -->
              <p *ngIf="roleErrorFor(u)" class="text-[11.5px] text-rose-500 mt-1 leading-relaxed">{{ roleErrorFor(u) }}</p>
              <div class="flex items-center gap-2 mt-1 flex-wrap">
                <p *ngIf="roleTrailLine(u.roleSetBy) as line" class="uw-audit">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-3 h-3" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                  {{ line }}
                </p>
                <button type="button" (click)="toggleHistory(u)" [attr.aria-expanded]="historyFor() === u.id"
                        class="text-[11px] font-medium text-(--uw-muted) underline decoration-dotted underline-offset-2 cursor-pointer hover:text-(--uw-strong)">
                  {{ historyFor() === u.id ? 'Hide history' : 'See full history' }}
                </button>
              </div>
            </div>

            <!-- Role pill. A falsy roleId is the SAME no-access state as 'inactive' to the gateway
                 (both denied as "no role assigned yet") — never render a role name the person does
                 not have, so both arms share this one check rather than a `|| 'admin'` fallback that
                 would overstate a locked-out person as the most privileged role in the project.
                 "No access" reads oddly because it still holds its seat — that's exactly the thing a
                 builder gets wrong, so the tooltip says so. Rows here can be End users, not only
                 Builders, so this is never labelled "Builder role" — see hasNoAccess()'s comment. -->
            <!-- Role picker — owners and editors (roleChangeControl). Same pill, same marks, with the
                 choice inside it: admin first, the project's roles, No access last. Read through
                 [selected], not ngModel — changeRole() puts the element back itself on a refusal. -->
            <span *ngIf="savingRole() === u.id" class="uw-live shrink-0" aria-live="polite"><i></i>Saving…</span>
            <label *ngIf="roleControl(u).enabled"
                   [title]="pickerTitle(u)"
                   class="uw-pill uw-role-pick shrink-0" [class.uw-pill-warn]="hasNoAccess(u)">
              <svg *ngIf="hasNoAccess(u)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" class="w-3 h-3" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="m5.7 5.7 12.6 12.6"/></svg>
              <svg *ngIf="u.roleId === 'admin'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3 text-amber-500" aria-hidden="true"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm0 3a1 1 0 011-1h12a1 1 0 011 1v1H5v-1z"/></svg>
              <i *ngIf="!hasNoAccess(u) && u.roleId !== 'admin'" class="uw-dot" [ngClass]="roleDot(u.roleId)" aria-hidden="true"></i>
              <select #roleSel (change)="changeRole(u, roleSel)" [disabled]="!!savingRole() || !!pendingAdmin()"
                      [attr.aria-label]="'Role of ' + displayName(u)">
                <option *ngFor="let o of roleChoices(u)" [value]="o.id" [selected]="o.id === roleValue(u)" [disabled]="o.disabled">{{ o.label }}</option>
              </select>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-2.5 h-2.5 opacity-60 pointer-events-none" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
            </label>

            <!-- Read-only pill — a viewer, or an editor looking at another builder (the reason is the tooltip). -->
            <span *ngIf="!roleControl(u).enabled && hasNoAccess(u)"
                  [title]="'No access — still holds a seat until they are removed. ' + roleControl(u).reason"
                  class="uw-pill uw-pill-warn shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" class="w-3 h-3" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="m5.7 5.7 12.6 12.6"/></svg>
              No access
            </span>
            <span *ngIf="!roleControl(u).enabled && !hasNoAccess(u)"
                  [title]="'Role in this app. ' + roleControl(u).reason"
                  class="uw-pill shrink-0" [class.capitalize]="!roleIsNamed(u.roleId)">
              <svg *ngIf="u.roleId === 'admin'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3 text-amber-500" aria-hidden="true"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm0 3a1 1 0 011-1h12a1 1 0 011 1v1H5v-1z"/></svg>
              <i *ngIf="u.roleId !== 'admin'" class="uw-dot" [ngClass]="roleDot(u.roleId)" aria-hidden="true"></i>
              {{ roleLabel(u.roleId) }}
            </span>

            <button *ngIf="canSetPassword(u) && !readOnly()" type="button" (click)="openSetPassword(u)" title="Set a new password"
                    class="uw-btn uw-btn-ghost shrink-0 h-8 px-2 text-[11.5px]">Set password</button>

            <!-- ⋯ — Transfer and Erase (owner, 2026-10-03). Erase can't be undone, so it is never one stray click away. -->
            <div *ngIf="!readOnly()" class="relative shrink-0">
              <button type="button" (click)="toggleMenu(u)" [attr.aria-expanded]="menuFor() === u.id" title="More"
                      class="uw-btn uw-btn-ghost h-8 px-2 text-[13px]">⋯</button>
              <div *ngIf="menuFor() === u.id" class="absolute right-0 top-9 z-20 min-w-[11rem] uw-modal p-1 text-[12.5px]">
                <button *ngIf="canEditDetails(access)" type="button" (click)="openEditName(u)" class="block w-full text-left px-3 py-2 rounded-lg hover:bg-(--uw-sunken)">Edit name…</button>
                <button *ngIf="canAddEmail(u, access)" type="button" (click)="openAddEmail(u)" class="block w-full text-left px-3 py-2 rounded-lg hover:bg-(--uw-sunken)">Add email…</button>
                <button type="button" (click)="openTransfer(u)" class="block w-full text-left px-3 py-2 rounded-lg hover:bg-(--uw-sunken)">Transfer work…</button>
                <button *ngIf="actions(u).erase" type="button" (click)="openErase(u)" class="block w-full text-left px-3 py-2 rounded-lg text-rose-600 hover:bg-rose-500/10">Erase…</button>
              </div>
            </div>

            <!-- Remove — always there as a quiet mark, so it can be found without hunting; its word
                 slides out when the row is hovered or the button is focused (the modal confirms). -->
            <button *ngIf="!readOnly()" type="button" (click)="openRemove(u)" title="Remove from workspace"
                    class="uw-btn uw-btn-danger-ghost uw-remove shrink-0 h-8 px-2 text-[11.5px]">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 11h-6"/></svg>
              <span class="uw-remove-label">Remove</span>
            </button>
          </div>
          <p *ngIf="actionError()?.id === u.id" class="uw-callout uw-callout-danger mt-2 text-[12px]">{{ actionError()!.text }}</p>
          <ng-container *ngIf="historyFor() === u.id"><ng-container *ngTemplateOutlet="historyPanel"></ng-container></ng-container>
          </ng-container>
        </div>

        <!-- ═══ Removed, behind their own toggle — hidden entirely when there are none. Narrowed to
             visibleRemovedPeople() (fix-round-1 #3): filterWorkspace's own comment requires the
             removed list narrow alongside the active one, since a removed person is still a person a
             search can find — showing "Removed (3)" here with none of the 3 actually matching would
             misdescribe what toggling it open reveals. ═══ -->
        <div *ngIf="visibleRemovedPeople().length > 0" class="mt-3">
          <button type="button" (click)="toggleRemoved()" class="uw-disclosure" [attr.aria-expanded]="showRemoved()">
            <svg class="h-3 w-3 shrink-0" [class.rotate-90]="showRemoved()"
                 fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
            </svg>
            Removed ({{ visibleRemovedPeople().length }})
          </button>

          <div *ngIf="showRemoved()" class="mt-2 space-y-1.5 uw-reveal">
            <p class="text-[11.5px] text-(--uw-muted) mb-2 px-1 leading-relaxed">
              Removed people keep their record so their name still shows on what they made. They hold no seat and cannot sign in.
            </p>
            <ng-container *ngFor="let u of visibleRemovedPeople()"><div class="uw-row is-dim">
              <div class="uw-avatar" [ngClass]="avatarFill(u)">{{ initials(u) }}</div>
              <div class="uw-row-text min-w-0 flex-1">
                <div class="text-[13px] font-medium text-(--uw-strong) truncate">{{ isErased(u) ? 'Erased person' : displayName(u) }}</div>
                <div class="text-[11.5px] text-(--uw-muted) truncate">{{ u.email || u.loginUsername || '—' }}</div>
                <button type="button" (click)="toggleHistory(u)" [attr.aria-expanded]="historyFor() === u.id"
                        class="mt-1 text-[11px] font-medium text-(--uw-muted) underline decoration-dotted underline-offset-2 cursor-pointer hover:text-(--uw-strong)">
                  {{ historyFor() === u.id ? 'Hide history' : 'See full history' }}
                </button>
              </div>
              <span *ngIf="isErased(u)" class="uw-chip shrink-0" title="Their details were cleared. Only their work can still be transferred.">Erased</span>
              <span class="uw-chip shrink-0">{{ removedDate(u) }}</span>
              <button *ngIf="actions(u).reinstate && !readOnly()" type="button" (click)="openReinstate(u)"
                      class="uw-btn uw-btn-ghost shrink-0 h-8 px-2 text-[11.5px]">Reinstate</button>
              <div *ngIf="!readOnly()" class="relative shrink-0">
                <button type="button" (click)="toggleMenu(u)" [attr.aria-expanded]="menuFor() === u.id" title="More"
                        class="uw-btn uw-btn-ghost h-8 px-2 text-[13px]">⋯</button>
                <div *ngIf="menuFor() === u.id" class="absolute right-0 top-9 z-20 min-w-[11rem] uw-modal p-1 text-[12.5px]">
                  <button type="button" (click)="openTransfer(u)" class="block w-full text-left px-3 py-2 rounded-lg hover:bg-(--uw-sunken)">Transfer work…</button>
                  <button *ngIf="actions(u).erase" type="button" (click)="openErase(u)" class="block w-full text-left px-3 py-2 rounded-lg text-rose-600 hover:bg-rose-500/10">Erase…</button>
                </div>
              </div>
            </div>
            <p *ngIf="actionError()?.id === u.id" class="uw-callout uw-callout-danger mt-2 text-[12px]">{{ actionError()!.text }}</p>
            <ng-container *ngIf="historyFor() === u.id"><ng-container *ngTemplateOutlet="historyPanel"></ng-container></ng-container>
            </ng-container>
          </div>
        </div>
      </ng-container>
    </div>

    <!-- ═══ Invitations — own lazy load, independent of the people list above ═══ -->
    <div class="mt-6">

      <div *ngIf="invitesError()" class="uw-callout uw-callout-danger mb-3">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-4 h-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4.5M12 16h.01"/></svg>
        <span class="flex-1">{{ invitesError() }}</span>
        <button type="button" (click)="loadInvitations()" class="shrink-0 font-semibold underline underline-offset-2 cursor-pointer">Try again</button>
      </div>

      <div *ngIf="invitesLoading()" class="space-y-2" aria-hidden="true">
        <div class="uw-skel is-line h-3 w-24"></div>
        <div *ngFor="let i of [1,2]" class="uw-skel h-[64px]"></div>
      </div>

      <ng-container *ngIf="invitesLoaded">
        <div class="uw-subhead">
          <h3>Invitations</h3>
          <p *ngIf="refreshingInvites()" class="uw-live" aria-live="polite"><i></i>Refreshing…</p>
        </div>

        <!-- Written empty states — the second explains why there is no invite control to use, without
             claiming it explains an empty LIST (fix-round-1 finding 6: the switch does not account for
             invitations sent before it was flipped — it just happens there are none of those either
             when this specific message shows, since it only shows when the list is truly empty). -->
        <p *ngIf="invitations().length === 0 && !invitesEmptyReason()" class="text-xs text-(--uw-muted) px-1 py-1">
          No invitations for this workspace yet.
        </p>
        <p *ngIf="invitations().length === 0 && invitesEmptyReason()" class="text-xs text-(--uw-muted) px-1 py-1">
          {{ invitesEmptyReason() }}
        </p>

        <ng-container *ngIf="invitations().length > 0">
          <!-- ═══ Pending, shown by default — its own written empty state when everything on record
               has already settled (fix-round-1 finding 5): otherwise this reads as "Invitations" then
               straight to "Show history (n)" with nothing said about there being none waiting. ═══ -->
          <p *ngIf="pendingInvitations().length === 0" class="text-xs text-(--uw-muted) px-1 py-1">
            Nothing is waiting — every invitation here has already been accepted, cancelled, or settled.
          </p>
          <!-- WHY EVERY RESEND BELOW IS GREYED OUT (fix-round-3 Important 3). Said once, above the
               list, rather than repeated on each row: it is one fact about the door, not about any
               one invitation. The same sentence the settings strip and the add-person form show,
               because all three read `doors().invite`. Cancel is still available on every row —
               invitations already sent can always be called off. -->
          <p *ngIf="pendingInvitations().length > 0 && resendBlockedLine(resendControl()) as line"
             class="uw-reason mb-2 px-1">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-3 h-3" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
            <span>{{ line }}</span>
          </p>
          <div *ngIf="pendingInvitations().length > 0" class="space-y-1.5">
            <ng-container *ngFor="let inv of pendingInvitations()">
              <ng-container *ngTemplateOutlet="inviteRow; context: { $implicit: inv, faded: false }"></ng-container>
            </ng-container>
          </div>

          <!-- ═══ History, behind its own toggle — hidden entirely when there is none ═══ -->
          <div *ngIf="historyInvitations().length > 0" class="mt-3">
            <button type="button" (click)="toggleInviteHistory()" class="uw-disclosure" [attr.aria-expanded]="showInviteHistory()">
              <svg class="h-3 w-3 shrink-0" [class.rotate-90]="showInviteHistory()"
                   fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
              </svg>
              Show history ({{ historyInvitations().length }})
            </button>

            <div *ngIf="showInviteHistory()" class="mt-2 space-y-1.5 uw-reveal">
              <ng-container *ngFor="let inv of historyInvitations()">
                <ng-container *ngTemplateOutlet="inviteRow; context: { $implicit: inv, faded: true }"></ng-container>
              </ng-container>
            </div>
          </div>
        </ng-container>
      </ng-container>
    </div>
  </div>
</div>

<!-- ═══ One invitation row — used for both the pending list and the history list (fix-round-1
     finding 4: two ~25-line copies of the same row, one of them guarding for actions `invitationRow`
     already guarantees are empty, is how the two stop agreeing). `faded` is the only visual
     difference; everything else — including whether actions render at all — comes from
     `invitationRow(...).actions`, so a history row simply renders none rather than needing its own
     check for it. ═══ -->
<ng-template #inviteRow let-inv let-faded="faded">
  <div class="uw-row is-top" [class.is-faded]="faded">
    <div class="uw-avatar uw-avatar-quiet">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
    </div>
    <div class="min-w-0 flex-1">
      <div class="flex items-center gap-2 flex-wrap">
        <span class="text-[13px] font-medium text-(--uw-strong) truncate">{{ inv.email }}</span>

        <!-- Same no-access decision as the people list's role pill (`roleIsNoAccess`, workspaces.util.ts)
             — fix-round-1 finding 2: this used to say "No role" here and "No access" six lines up for
             the exact same empty roleId. -->
        <span *ngIf="inviteHasNoAccess(inv)"
              title="No access — they would join with no role assigned until one is given."
              class="uw-pill uw-pill-warn is-sm">
          No access
        </span>
        <span *ngIf="!inviteHasNoAccess(inv)" class="uw-pill is-sm" [class.capitalize]="!roleIsNamed(inv.roleId)">
          <i class="uw-dot is-sm" [ngClass]="roleDot(inv.roleId)" aria-hidden="true"></i>
          {{ roleLabel(inv.roleId) }}
        </span>
      </div>

      <!-- The hint sits on every row, not on hover — this is the whole reason the list exists. -->
      <p class="text-[11.5px] text-(--uw-muted) mt-1 leading-relaxed">{{ invitationRow(inv, { seatsFull: seatsAreFull(workspace) }).hint }}</p>
      <!-- Invitations carry a role but no picker (owner, 2026-09-25): say where it CAN change. -->
      <p *ngIf="inv.status === 'pending'" class="text-[11.5px] text-(--uw-muted) mt-1 leading-relaxed">{{ INVITE_ROLE_LATER }}</p>
      <p class="text-[11px] text-(--uw-faint) mt-1 truncate">{{ invitedLine(inv) }}</p>
      <p *ngIf="inviteNoticeFor(inv)" class="text-[11.5px] uw-text-warn mt-1 leading-relaxed">{{ inviteNoticeFor(inv) }}</p>
      <p *ngIf="inviteErrorFor(inv)" class="text-[11.5px] text-rose-500 mt-1 leading-relaxed">{{ inviteErrorFor(inv) }}</p>
    </div>
    <div class="shrink-0 flex flex-col items-end gap-2">
      <span class="uw-status" [ngClass]="inviteTone(inv)">
        {{ invitationRow(inv, { seatsFull: seatsAreFull(workspace) }).label }}
      </span>
      <div class="flex items-center gap-1">
        <!-- [disabled] is `!!busyInvite()`, matching the per-component guard at the top of
             `resendInvite`/`cancelInvite` exactly (fix-round-1 finding 1) — a per-id disabled next to
             a per-component guard let every OTHER row's buttons look clickable while one call was in
             flight and silently do nothing. The whole list quiesces for the length of one call. -->
        <!-- Resend is `inviteUser` again, so the invitation door decides whether it can work at all
             (fix-round-3 Important 3) — `actions.includes('resend')` only knows the invitation is
             pending. The disabled button carries `doors().invite`'s OWN reason, so it agrees word
             for word with the settings strip and the add-person form above it, instead of leaving
             the server to answer with a sentence written about the PROJECT switch. Cancel stays
             ungated on purpose — no door closes it — except on a workspace being deleted, which is
             read-only throughout. -->
        <button *ngIf="invitationRow(inv, { seatsFull: seatsAreFull(workspace) }).actions.includes('resend')"
                type="button" (click)="resendInvite(inv)"
                [disabled]="!!busyInvite() || !resendControl().enabled"
                [attr.title]="resendControl().enabled ? null : resendControl().reason"
                class="uw-btn uw-btn-ghost h-7 px-2 text-[11.5px]">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" class="w-3 h-3" aria-hidden="true"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>
          {{ isInviteBusyWith(inv, 'resend') ? 'Sending…' : 'Resend' }}
        </button>
        <button *ngIf="invitationRow(inv, { seatsFull: seatsAreFull(workspace) }).actions.includes('cancel') && !readOnly()"
                type="button" (click)="cancelInvite(inv)" [disabled]="!!busyInvite()"
                class="uw-btn uw-btn-danger-ghost h-7 px-2 text-[11.5px]">
          {{ isInviteBusyWith(inv, 'cancel') ? 'Cancelling…' : 'Cancel' }}
        </button>
      </div>
    </div>
  </div>
</ng-template>

<!-- ═══ One person's role history — newest first, one continuous record across removal and return. ═══ -->
<ng-template #historyPanel>
  <div class="ml-12 mt-1 mb-2 px-3 py-2.5 rounded-xl border border-(--uw-line) bg-(--uw-sunken) uw-reveal" aria-live="polite">
    <div *ngIf="historyLoading()" class="space-y-1.5" aria-hidden="true">
      <div *ngFor="let i of [1, 2]" class="uw-skel is-line h-3 w-48"></div>
    </div>
    <p *ngIf="historyError()" class="text-[11.5px] text-rose-500">{{ historyError() }}</p>
    <ng-container *ngIf="!historyLoading() && history() as h">
      <p class="text-[11px] text-(--uw-faint) mb-2">{{ roleHistorySince(h.startedAt) }}</p>
      <p *ngIf="historyEntries().length === 0" class="text-xs text-(--uw-muted)">No role changes since then.</p>
      <ol *ngIf="historyEntries().length > 0" class="space-y-1.5">
        <li *ngFor="let e of historyEntries()" class="text-[12px] leading-relaxed">
          <span class="font-medium text-(--uw-strong)">{{ e.what }}</span>
          <span class="text-(--uw-muted)"> {{ e.who }}</span>
          <span *ngIf="e.when" class="text-(--uw-faint)"> · {{ e.when }}</span>
        </li>
      </ol>
    </ng-container>
  </div>
</ng-template>

<!-- ═══ Removal confirmation — a modal, no type-to-confirm: this names the person and states the
     consequences, which the spec judged enough. Modelled on pages.component.html's delete-page
     modal (~line 300) but without its type-to-confirm input. ═══ -->
<div *ngIf="removeTarget() as target" class="fixed inset-0 z-50 flex items-center justify-center p-4 uw-scrim uw-fade"
     (click)="cancelRemove()">
  <div class="uw-modal uw-pop max-w-md w-full overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="uw-remove-title"
       (click)="$event.stopPropagation()">
    <div class="px-6 pt-6 pb-5">
      <div class="flex items-start justify-between gap-4 mb-5">
        <div class="flex items-center gap-3">
          <div class="h-10 w-10 rounded-xl flex items-center justify-center bg-rose-500/10 text-rose-500 ring-1 ring-inset ring-rose-500/25">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-[18px] w-[18px]"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 11h-6"/></svg>
          </div>
          <h3 id="uw-remove-title" class="text-[15px] font-semibold text-(--uw-strong)">Remove from workspace</h3>
        </div>
        <button type="button" (click)="cancelRemove()" [disabled]="removing()" aria-label="Close"
                class="uw-btn uw-btn-ghost h-8 w-8">
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>

      <!-- Who, and from where — the person as they appear in the list, then the question. -->
      <div class="flex items-center gap-3 p-3 rounded-xl bg-(--uw-sunken) border border-(--uw-line) mb-4">
        <div class="uw-avatar" [ngClass]="avatarFill(target)">{{ initials(target) }}</div>
        <div class="min-w-0">
          <div class="text-[13px] font-semibold text-(--uw-strong) truncate">{{ displayName(target) }}</div>
          <div class="text-[11.5px] text-(--uw-muted) truncate">{{ target.email || target.loginUsername || '—' }}</div>
        </div>
      </div>
      <div class="space-y-3 text-[13px] text-(--uw-text) leading-relaxed">
        <p>
          Remove <span class="font-semibold text-(--uw-strong)">{{ displayName(target) }}</span>
          from <span class="font-semibold text-(--uw-strong)">{{ workspace.name }}</span>?
        </p>
        <p class="text-(--uw-muted) text-[12.5px]">
          {{ REMOVE_PERSON_CONSEQUENCES }}
        </p>
      </div>
      <!-- Failure shown INSIDE the modal, which stays open — the two refusals a builder actually
           hits ("That person is not in this workspace." / the last-admin refusal) need reading. -->
      <p *ngIf="removeError()" class="uw-callout uw-callout-danger mt-4">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" class="w-4 h-4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4.5M12 16h.01"/></svg>
        <span>{{ removeError() }}</span>
      </p>
    </div>
    <div class="px-6 py-4 border-t border-(--uw-line) bg-(--uw-sunken) flex justify-end gap-2">
      <button type="button" (click)="cancelRemove()" [disabled]="removing()"
              class="uw-btn uw-btn-ghost h-9 px-4 text-[13px]">
        Cancel
      </button>
      <button type="button" (click)="confirmRemove()" [disabled]="removing()" [attr.data-mcp-button]="pressing() === 'remove-person' ? 'Remove' : null"
              class="uw-btn uw-btn-danger h-9 px-4 text-[13px]">
        {{ removing() ? 'Removing…' : 'Remove' }}
      </button>
    </div>
  </div>
</div>

<!-- ═══ Set password (Admin sets password) — a new password, shown once, and the person is signed
     out everywhere in this project. Nothing is emailed; the builder hands it over. ═══ -->
<div *ngIf="pwTarget() as target" class="fixed inset-0 z-50 flex items-center justify-center p-4 uw-scrim uw-fade"
     (click)="closeSetPassword()">
  <div class="uw-modal uw-pop max-w-md w-full overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="uw-pw-title"
       (click)="$event.stopPropagation()">
    <div class="px-6 pt-6 pb-5">
      <div class="flex items-start justify-between gap-4 mb-5">
        <h3 id="uw-pw-title" class="text-[15px] font-semibold text-(--uw-strong)">Set password</h3>
        <button type="button" (click)="closeSetPassword()" [disabled]="pwSaving()" aria-label="Close" class="uw-btn uw-btn-ghost h-8 w-8">
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>

      <div class="flex items-center gap-3 p-3 rounded-xl bg-(--uw-sunken) border border-(--uw-line) mb-4">
        <div class="uw-avatar" [ngClass]="avatarFill(target)">{{ initials(target) }}</div>
        <div class="min-w-0">
          <div class="text-[13px] font-semibold text-(--uw-strong) truncate">{{ displayName(target) }}</div>
          <div class="text-[11.5px] text-(--uw-muted) truncate">{{ target.email || target.loginUsername || '—' }}</div>
        </div>
      </div>

      <div class="flex items-center gap-2 mb-3">
        <code class="flex-1 px-3 py-2 rounded-lg border border-(--uw-line) text-[14px] tracking-wide select-all">{{ pwValue() }}</code>
        <button type="button" (click)="copySetPassword()" class="uw-btn uw-btn-ghost h-9 px-3 text-[12px]">{{ pwCopied() ? 'Copied' : 'Copy' }}</button>
        <button *ngIf="!pwDone()" type="button" (click)="regeneratePassword()" [disabled]="pwSaving()" class="uw-btn uw-btn-ghost h-9 px-3 text-[12px]">New one</button>
      </div>

      <p *ngIf="!pwDone()" class="text-[12.5px] text-(--uw-text) leading-relaxed">
        This replaces their password. They'll be signed out of this app on every device within the hour, and
        nothing is emailed — copy it and hand it to them yourself. It is shown only here.
      </p>
      <p *ngIf="pwDone()" class="text-[12.5px] text-(--uw-strong) leading-relaxed">
        Password set. Copy it now — once you close this, it can't be shown again.
      </p>
      <p *ngIf="pwError()" class="mt-3 text-[12.5px] text-rose-600">{{ pwError() }}</p>
    </div>

    <div class="px-6 py-4 border-t border-(--uw-line) flex justify-end gap-2">
      <ng-container *ngIf="!pwDone(); else pwClose">
        <button type="button" (click)="closeSetPassword()" [disabled]="pwSaving()" class="uw-btn uw-btn-ghost h-9 px-4 text-[13px]">Cancel</button>
        <button type="button" (click)="confirmSetPassword()" [disabled]="pwSaving()" [attr.data-mcp-button]="pressing() === 'set-person-password' ? 'Set password' : null" class="uw-btn uw-btn-primary h-9 px-4 text-[13px]">
          {{ pwSaving() ? 'Setting…' : 'Set password' }}</button>
      </ng-container>
      <ng-template #pwClose>
        <button type="button" (click)="closeSetPassword()" class="uw-btn uw-btn-primary h-9 px-4 text-[13px]">Done</button>
      </ng-template>
    </div>
  </div>
</div>

<!-- ═══ ws 9b-3 W11 — Edit name / Add email. One small modal; the server's sentence stays inside it on failure. ═══ -->
<div *ngIf="detailTarget() as target" class="fixed inset-0 z-50 flex items-center justify-center p-4 uw-scrim uw-fade" (click)="closeDetail()">
  <div class="uw-modal uw-pop max-w-md w-full overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="uw-detail-title" (click)="$event.stopPropagation()">
    <div class="px-6 pt-6 pb-5">
      <div class="flex items-start justify-between gap-4 mb-5">
        <h3 id="uw-detail-title" class="text-[15px] font-semibold text-(--uw-strong)">{{ detailMode() === 'name' ? 'Edit name' : 'Add an email' }}</h3>
        <button type="button" (click)="closeDetail()" [disabled]="detailSaving()" aria-label="Close" class="uw-btn uw-btn-ghost h-8 w-8">
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <div class="flex items-center gap-3 p-3 rounded-xl bg-(--uw-sunken) border border-(--uw-line) mb-4">
        <div class="uw-avatar" [ngClass]="avatarFill(target)">{{ initials(target) }}</div>
        <div class="min-w-0">
          <div class="text-[13px] font-semibold text-(--uw-strong) truncate">{{ displayName(target) }}</div>
          <div class="text-[11.5px] text-(--uw-muted) truncate">{{ target.email || target.loginUsername || '—' }}</div>
        </div>
      </div>
      <ng-container *ngIf="!detailDone(); else detailSent">
        <label class="uw-label" for="uw-detail-input">{{ detailMode() === 'name' ? 'Name' : 'Email' }}</label>
        <input id="uw-detail-input" [type]="detailMode() === 'name' ? 'text' : 'email'" [(ngModel)]="detailDraft" [attr.maxlength]="detailMode() === 'name' ? 80 : 254"
               (keydown.enter)="saveDetail()" [disabled]="detailSaving()" class="uw-field w-full h-10 px-3 text-[13px]">
        <p *ngIf="detailMode() === 'email'" class="mt-3 text-[12.5px] text-(--uw-text) leading-relaxed">{{ ADD_EMAIL_NOTE }}</p>
      </ng-container>
      <ng-template #detailSent><p class="text-[12.5px] text-(--uw-strong) leading-relaxed">{{ detailDone() }}</p></ng-template>
      <p *ngIf="detailError()" class="mt-3 text-[12.5px] text-rose-600">{{ detailError() }}</p>
    </div>
    <div class="px-6 py-4 border-t border-(--uw-line) flex justify-end gap-2">
      <ng-container *ngIf="!detailDone(); else detailClose">
        <button type="button" (click)="closeDetail()" [disabled]="detailSaving()" class="uw-btn uw-btn-ghost h-9 px-4 text-[13px]">Cancel</button>
        <button type="button" (click)="saveDetail()" [disabled]="detailSaving()" [attr.data-mcp-button]="pressing() === 'set-person-sign-in-email' ? 'Send link' : null" class="uw-btn uw-btn-primary h-9 px-4 text-[13px]">
          {{ detailSaving() ? 'Saving…' : (detailMode() === 'name' ? 'Save' : 'Send link') }}</button>
      </ng-container>
      <ng-template #detailClose>
        <button type="button" (click)="closeDetail()" class="uw-btn uw-btn-primary h-9 px-4 text-[13px]">Done</button>
      </ng-template>
    </div>
  </div>
</div>

<!-- ═══ Granting admin — always asks first (owner, 2026-09-25). Cancel puts the picker back. ═══ -->
<div *ngIf="pendingAdmin() as p" class="fixed inset-0 z-50 flex items-center justify-center p-4 uw-scrim uw-fade"
     (click)="cancelAdmin()">
  <div class="uw-modal uw-pop max-w-md w-full overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="uw-admin-title"
       (click)="$event.stopPropagation()">
    <div class="px-6 pt-6 pb-5">
      <div class="flex items-start justify-between gap-4 mb-5">
        <div class="flex items-center gap-3">
          <div class="h-10 w-10 rounded-xl flex items-center justify-center bg-amber-500/10 text-amber-500 ring-1 ring-inset ring-amber-500/25">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-[18px] w-[18px]" aria-hidden="true"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm0 3a1 1 0 011-1h12a1 1 0 011 1v1H5v-1z"/></svg>
          </div>
          <h3 id="uw-admin-title" class="text-[15px] font-semibold text-(--uw-strong)">{{ ADMIN_GRANT_TITLE }}</h3>
        </div>
        <button type="button" (click)="cancelAdmin()" aria-label="Close" class="uw-btn uw-btn-ghost h-8 w-8">
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <div class="flex items-center gap-3 p-3 rounded-xl bg-(--uw-sunken) border border-(--uw-line) mb-4">
        <div class="uw-avatar" [ngClass]="avatarFill(p.user)">{{ initials(p.user) }}</div>
        <div class="min-w-0">
          <div class="text-[13px] font-semibold text-(--uw-strong) truncate">{{ displayName(p.user) }}</div>
          <div class="text-[11.5px] text-(--uw-muted) truncate">{{ p.user.email || p.user.loginUsername || '—' }}</div>
        </div>
      </div>
      <p class="text-[13px] text-(--uw-text) leading-relaxed">{{ adminGrantBody(displayName(p.user), workspace.name) }}</p>
    </div>
    <div class="px-6 py-4 border-t border-(--uw-line) bg-(--uw-sunken) flex justify-end gap-2">
      <button type="button" (click)="cancelAdmin()" class="uw-btn uw-btn-ghost h-9 px-4 text-[13px]">Cancel</button>
      <button type="button" (click)="confirmAdmin()" class="uw-btn uw-btn-primary h-9 px-4 text-[13px]">Make admin</button>
    </div>
  </div>
</div>

<!-- ═══ Transfer work — choose who receives it and which records move; the preview and the confirm are the
     shared people overlay. ═══ -->
<div *ngIf="transferFrom() as from" class="fixed inset-0 z-50 flex items-center justify-center p-4 uw-scrim uw-fade" (click)="closeTransfer()">
  <div class="uw-modal uw-pop max-w-md w-full overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="uw-transfer-title" (click)="$event.stopPropagation()">
    <div class="px-6 pt-6 pb-5 space-y-4">
      <h3 id="uw-transfer-title" class="text-[15px] font-semibold text-(--uw-strong)">
        Transfer {{ isErased(from) ? 'an erased person' : displayName(from) }}'s work
      </h3>
      <label class="block text-[13px] text-(--uw-text)">To
        <select class="mt-1 block w-full uw-field h-9 px-2" [value]="transferTo()" (change)="transferTo.set($any($event.target).value)">
          <option value="" disabled>Choose who takes it over</option>
          <option *ngFor="let p of receivers()" [value]="p.id">{{ displayName(p) }}</option>
        </select>
      </label>
      <div class="text-[13px] text-(--uw-text)">
        <div class="mb-1">Records to move</div>
        <p *ngIf="!transferChoices().length" class="text-(--uw-muted) text-[12.5px]">Nothing in this app points at people.</p>
        <label *ngFor="let c of transferChoices()" class="flex items-center gap-2 py-1">
          <input type="checkbox" [checked]="transferPicked().has(c.id)" (change)="togglePicked(c.id)">
          <span>{{ c.name }}</span><span class="text-(--uw-muted)">({{ c.rows }})</span>
        </label>
      </div>
      <p *ngIf="transferError()" class="uw-callout uw-callout-danger">{{ transferError() }}</p>
    </div>
    <div class="px-6 py-4 border-t border-(--uw-line) bg-(--uw-sunken) flex justify-end gap-2">
      <button type="button" (click)="closeTransfer()" [disabled]="transferBusy()" class="uw-btn uw-btn-ghost h-9 px-4 text-[13px]">Cancel</button>
      <button type="button" (click)="previewTransfer()" [disabled]="transferBusy() || !transferTo() || !transferPicked().size"
              class="uw-btn uw-btn-primary h-9 px-4 text-[13px]">{{ transferBusy() ? 'Checking…' : 'Preview' }}</button>
    </div>
  </div>
</div>
